import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'

export interface TeamMember {
  id: string
  userId: string
  email: string
  name: string
  role: 'owner' | 'admin' | 'user'
  createdAt: string
  lastLoginAt: string | null
  eventsCount: number
  leadsCount: number
}

export interface Invitation {
  id: string
  email: string
  role: 'admin' | 'user'
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  expiresAt: string
  createdAt: string
}

export function useTeam() {
  const { user } = useAuth()
  const { activeWorkspace, isAdmin } = useWorkspace()

  return useQuery({
    queryKey: ['team', activeWorkspace?.id],
    queryFn: async (): Promise<{ members: TeamMember[]; invitations: Invitation[] }> => {
      if (!user || !activeWorkspace) return { members: [], invitations: [] }

      // Get all team members for this workspace
      const { data: teamData, error: teamError } = await supabase
        .from('tenant_users')
        .select('id, user_id, role, created_at')
        .eq('tenant_id', activeWorkspace.id)
        .order('created_at', { ascending: true })

      if (teamError) throw teamError

      // User profiles separat laden (vermeidet ambige Joins)
      const userIds = (teamData || []).map((t) => t.user_id)
      const { data: profiles } = userIds.length > 0
        ? await supabase
            .from('user_profiles')
            .select('id, email, name, last_login_at')
            .in('id', userIds)
        : { data: [] }

      const profileMap = new Map(
        (profiles || []).map((p) => [p.id, p])
      )

      // Event counts
      const { data: events } = await supabase
        .from('events')
        .select('created_by')
        .eq('tenant_id', activeWorkspace.id)

      const eventCounts: Record<string, number> = {}
      events?.forEach((e) => {
        eventCounts[e.created_by] = (eventCounts[e.created_by] || 0) + 1
      })

      const members: TeamMember[] = (teamData || []).map((tm) => {
        const profile = profileMap.get(tm.user_id)
        return {
          id: tm.id,
          userId: tm.user_id,
          email: profile?.email || '',
          name: profile?.name || '',
          role: tm.role as TeamMember['role'],
          createdAt: tm.created_at,
          lastLoginAt: profile?.last_login_at || null,
          eventsCount: eventCounts[tm.user_id] || 0,
          leadsCount: 0,
        }
      })

      // Get pending invitations
      const { data: invitationsData } = await supabase
        .from('invitations')
        .select('*')
        .eq('tenant_id', activeWorkspace.id)
        .in('status', ['pending'])
        .order('created_at', { ascending: false })

      const invitations: Invitation[] = (invitationsData || []).map((inv) => ({
        id: inv.id,
        email: inv.email,
        role: inv.role as Invitation['role'],
        status: inv.status as Invitation['status'],
        expiresAt: inv.expires_at,
        createdAt: inv.created_at,
      }))

      return { members, invitations }
    },
    enabled: !!user && !!activeWorkspace && isAdmin,
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: 'admin' | 'user' }) => {
      if (!user) throw new Error('Nicht angemeldet')
      if (!activeWorkspace) throw new Error('Kein Workspace ausgewählt')

      // Generate invitation token
      const token = crypto.randomUUID()
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days

      const { data, error } = await supabase
        .from('invitations')
        .insert({
          tenant_id: activeWorkspace.id,
          email,
          role,
          token,
          status: 'pending',
          invited_by: user.id,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single()

      if (error) throw error

      // TODO: Send invitation email via Edge Function
      return { ...data, token }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useBulkInvite() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useMutation({
    mutationFn: async ({
      invites,
      customText,
    }: {
      invites: Array<{ email: string; firma: string; ansprechpartner: string }>
      customText: string
    }): Promise<{ success: number; failed: number }> => {
      if (!user) throw new Error('Nicht angemeldet')
      if (!activeWorkspace) throw new Error('Kein Workspace ausgewählt')

      let success = 0
      let failed = 0

      for (const invite of invites) {
        try {
          // Prüfen ob E-Mail schon eingeladen oder Mitglied
          const { data: existing } = await supabase
            .from('invitations')
            .select('id')
            .eq('tenant_id', activeWorkspace.id)
            .eq('email', invite.email)
            .eq('status', 'pending')
            .maybeSingle()

          if (existing) {
            failed++
            continue
          }

          const token = crypto.randomUUID()
          const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)

          const { error } = await supabase.from('invitations').insert({
            tenant_id: activeWorkspace.id,
            email: invite.email,
            role: 'user',
            token,
            status: 'pending',
            invited_by: user.id,
            expires_at: expiresAt.toISOString(),
          })

          if (error) {
            failed++
          } else {
            success++
          }
        } catch {
          failed++
        }
      }

      return { success, failed }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'revoked' })
        .eq('id', invitationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: 'admin' | 'user' }) => {
      const { error } = await supabase
        .from('tenant_users')
        .update({ role })
        .eq('id', memberId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('tenant_users')
        .delete()
        .eq('id', memberId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}
